import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  BottomNavigation,
  BottomNavigationAction,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, switchParish } from '../../features/auth/authSlice';
import { AppDispatch, RootState } from '../../store';
import { getCurrentAuthContext } from '../../services/auth';
import { usePermissions } from '../../permissions';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
// import MessageIcon from '@mui/icons-material/Message';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import CheckIcon from '@mui/icons-material/Check';
import { PlatformChromeProvider, usePlatformChrome } from '../../context/PlatformChromeContext';
import { useBrand } from '../../brands/BrandProvider';
import { AUDIENCE_FUNNEL_UI_ENABLED } from '../../features/audience-funnel/featureFlag';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

const MainLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <PlatformChromeProvider>
      <MainLayoutInner>{children}</MainLayoutInner>
    </PlatformChromeProvider>
  );
};

const MainLayoutInner: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { mode: chromeMode, organizerName } = usePlatformChrome();
  const isNeutralChrome = chromeMode === 'neutral';
  const brand = useBrand();
  const isCustomBrand = brand.key !== 'platform';
  const brandLogo = brand.logoUrl;
  const brandName = brand.displayName;
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const authContext = getCurrentAuthContext();

  const [topbarMenuAnchorEl, setTopbarMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [sidebarMenuAnchorEl, setSidebarMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [orgMenuAnchorEl, setOrgMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [switchingOrg, setSwitchingOrg] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const organizations = user?.organizations || [];
  const activeOrgId = user?.parishId ? String(user.parishId) : user?.parish?.id ? String(user.parish.id) : '';
  const activeOrgName =
    organizations.find((o) => String(o._id) === activeOrgId)?.name ||
    user?.parish?.name ||
    '';
  const canAddOrganization =
    user?.userType === 'parish_admin' && user?.allowAdditionalOrganizations === true;
  const showOrgSwitcher =
    user?.userType === 'parish_admin' && (organizations.length > 1 || canAddOrganization);

  const handleTopbarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTopbarMenuAnchorEl(event.currentTarget);
  };

  const handleSidebarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSidebarMenuAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setTopbarMenuAnchorEl(null);
    setSidebarMenuAnchorEl(null);
    setOrgMenuAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    handleClose();
  };

  const handleSwitchOrganization = async (parishId: string) => {
    if (!parishId || parishId === activeOrgId || switchingOrg) {
      setOrgMenuAnchorEl(null);
      return;
    }
    setSwitchingOrg(true);
    try {
      await dispatch(switchParish(parishId)).unwrap();
      setOrgMenuAnchorEl(null);
      window.location.assign('/dashboard');
    } catch {
      setSwitchingOrg(false);
      setOrgMenuAnchorEl(null);
    }
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setDrawerOpen(open);
  };

  const permissions = usePermissions();

  const showParishOnboardingEntry = user?.userType === 'parish_admin';
  const showOnboardingBanner =
    Boolean(
      showParishOnboardingEntry &&
        user?.parishAdminOnboarding &&
        user.parishAdminOnboarding.progressPercent < 100 &&
        !location.pathname.startsWith('/dashboard/onboarding')
    );

  // Menu do painel (drawer/sidebar): Saque fica na aba do evento, não aqui
  const showLandingPage =
    user?.parish?.features?.customDomainPagesEnabled === true ||
    user?.parish?.brandKey === 'zuadaria' ||
    brand.key === 'zuadaria';
  const drawerItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    ...(showParishOnboardingEntry
      ? [{ text: 'Concluir cadastro', icon: <TaskAltRoundedIcon />, path: '/dashboard/onboarding' }]
      : []),
    { text: 'Eventos', icon: <EventIcon />, path: '/dashboard/eventos' },
    ...(showLandingPage
      ? [{ text: 'Minha página', icon: <LanguageIcon />, path: '/dashboard/minha-pagina' }]
      : []),
    ...(AUDIENCE_FUNNEL_UI_ENABLED
      ? [{ text: 'Novidades', icon: <NewReleasesIcon />, path: '/dashboard/novidades' }]
      : []),
    ...(permissions.manage_subparishes
      ? [{ text: 'Setores', icon: <AccountTreeIcon />, path: '/dashboard/subparoquias' }]
      : []),
    { text: 'Configurações', icon: <SettingsIcon />, path: '/dashboard/perfil' },
  ];
  const dashboardSidebarWidth = 248;

  const dashboardMenuContent = (
    <Box
      sx={{
        width: dashboardSidebarWidth,
        backgroundColor: '#f8fafc',
        height: '100%',
        color: 'text.primary',
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
      role="presentation"
    >
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {brand.copy.chrome.sidebarTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {brand.copy.chrome.sidebarSubtitle}
        </Typography>
        {showOrgSwitcher && (
          <Box sx={{ mt: 1.5 }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              endIcon={switchingOrg ? <CircularProgress size={14} /> : <KeyboardArrowDownIcon />}
              disabled={switchingOrg}
              onClick={(e) => setOrgMenuAnchorEl(e.currentTarget)}
              sx={{
                justifyContent: 'space-between',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 0.85,
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.1}>
                  Organização
                </Typography>
                <Typography variant="body2" noWrap fontWeight={700}>
                  {activeOrgName || 'Selecionar'}
                </Typography>
              </Box>
            </Button>
            <Menu
              anchorEl={orgMenuAnchorEl}
              open={Boolean(orgMenuAnchorEl)}
              onClose={() => setOrgMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { minWidth: 220, maxWidth: 280, mt: 0.5 } }}
            >
              {organizations.map((org) => {
                const id = String(org._id);
                const selected = id === activeOrgId;
                return (
                  <MenuItem
                    key={id}
                    selected={selected}
                    onClick={() => void handleSwitchOrganization(id)}
                    sx={{ gap: 1 }}
                  >
                    {selected ? <CheckIcon fontSize="small" color="primary" /> : <Box sx={{ width: 20 }} />}
                    <Typography variant="body2" noWrap>
                      {org.name}
                    </Typography>
                  </MenuItem>
                );
              })}
              {canAddOrganization && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={() => {
                      setOrgMenuAnchorEl(null);
                      navigate('/dashboard/adicionar-organizacao');
                    }}
                    sx={{ gap: 1 }}
                  >
                    <AddBusinessIcon fontSize="small" />
                    <Typography variant="body2">Adicionar organização</Typography>
                  </MenuItem>
                </>
              )}
            </Menu>
          </Box>
        )}
        {!showOrgSwitcher && canAddOrganization && (
          <Button
            fullWidth
            size="small"
            startIcon={<AddBusinessIcon />}
            onClick={() => navigate('/dashboard/adicionar-organizacao')}
            sx={{ mt: 1.5, textTransform: 'none', justifyContent: 'flex-start' }}
          >
            Adicionar organização
          </Button>
        )}
      </Box>
      <List sx={{ px: 1.5, py: 1 }}>
        {drawerItems.map((item) => {
          const audienceModulePaths = [
            '/dashboard/novidades',
            '/dashboard/publico',
            '/dashboard/filtros',
            '/dashboard/funil',
          ];
          const isActive =
            item.path === '/dashboard/novidades'
              ? audienceModulePaths.some(
                  (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
                )
              : item.path === '/dashboard'
                ? location.pathname === '/dashboard'
                : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: 2,
                  ...(isActive
                    ? {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '& .MuiListItemIcon-root': { color: 'white' },
                      }
                    : {
                        color: 'text.secondary',
                        '& .MuiListItemIcon-root': { color: 'text.secondary' },
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }),
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ mt: 'auto', p: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2.5,
            bgcolor: '#eef2f7',
            borderColor: 'transparent',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#e8edf4' },
          }}
          onClick={handleSidebarMenuOpen}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar alt={user?.name} sx={{ width: 42, height: 42 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {user?.name || 'Usuário'}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                Administrador
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSidebarMenuOpen(e);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  // Detectar se está na rota pública de evento: /:eventSlug
  const isEventPublicPage = /^\/[^/]+$/.test(location.pathname);
  
  // Detectar se está em uma rota do dashboard
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const isParishDashboardShell = isDashboardRoute && isAuthenticated && authContext === 'parish';
  const isPublicGuest = !isAuthenticated && !isDashboardRoute;
  const isClubPublic = brand.key === 'zuadaria' && isPublicGuest;
  const mobileBottomItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <HomeRoundedIcon /> },
    { label: 'Eventos', path: '/dashboard/eventos', icon: <EventIcon /> },
    { label: 'Config.', path: '/dashboard/perfil', icon: <SettingsIcon /> },
  ];
  const mobileBottomValue =
    mobileBottomItems.find((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))?.path ||
    '/dashboard';

  return (
    <>
      {/* AppBar condicional - sempre mostrar em rotas do dashboard */}
      {(isDashboardRoute || !isEventPublicPage) && (
        <AppBar 
          position="static" 
          sx={{ 
            display: isParishDashboardShell ? 'none' : 'block',
            background: isPublicGuest ? alpha('#ffffff', 0.92) : alpha('#ffffff', 0.88),
            backdropFilter: 'blur(18px)',
            boxShadow: isPublicGuest ? '0 4px 14px rgba(31, 39, 53, 0.06)' : '0 6px 18px rgba(31, 39, 53, 0.08)',
            borderBottom: `1px solid ${alpha('#2b2340', isPublicGuest ? 0.07 : 0.10)}`,
            color: '#2b2340',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar
              disableGutters
              sx={{
                gap: 1.5,
                minHeight: isClubPublic ? 64 : isPublicGuest ? 56 : 68,
                py: isClubPublic ? 0.75 : 0,
                '&.MuiToolbar-root': {
                  minHeight: isClubPublic ? 64 : undefined,
                },
              }}
            >
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 1 }}
              >
                <Box
                  component="img"
                  src={brandLogo}
                  alt={`Logo ${brandName}`}
                  sx={{
                    width: isClubPublic ? 'auto' : isPublicGuest ? 118 : 104,
                    height: isClubPublic ? { xs: 40, md: 48 } : isPublicGuest ? 26 : 23,
                    maxWidth: isClubPublic ? { xs: 160, md: 200 } : undefined,
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    display: 'block',
                  }}
                />
              </Box>
              {isClubPublic && (
                <Button
                  component={Link}
                  to="/cadastro-paroquia"
                  sx={{
                    color: '#111111',
                    fontWeight: 700,
                    textTransform: 'none',
                    bgcolor: '#E6A532',
                    borderRadius: 999,
                    px: 1.8,
                    py: 0.6,
                    fontSize: { xs: '0.72rem', md: '0.8rem' },
                    '&:hover': { bgcolor: '#F0B84A' },
                  }}
                >
                  Cadastrar show
                </Button>
              )}
              <Box sx={{ mr: 1.5, display: { xs: 'none', md: 'flex' }, width: 8 }} />

              {isAuthenticated && (
                <IconButton
                  size="large"
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ mr: 2, display: { xs: 'flex', md: 'none' } }}
                  onClick={toggleDrawer(true)}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />

              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                {isAuthenticated && authContext === 'parish' && (
                  <>
                    <Button 
                      component={Link} 
                      to="/dashboard" 
                      sx={{ 
                        my: 2, 
                        color: '#2b2340',
                        borderRadius: 2.5,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.09),
                          color: theme.palette.primary.main,
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Dashboard
                    </Button>
                    <Button 
                      component={Link} 
                      to="/dashboard/eventos" 
                      sx={{ 
                        my: 2, 
                        color: '#2b2340',
                        borderRadius: 2.5,
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.09),
                          color: theme.palette.primary.main,
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Eventos
                    </Button>
                    {permissions.manage_subparishes && (
                      <Button 
                        component={Link} 
                        to="/dashboard/subparoquias" 
                        sx={{ 
                          my: 2, 
                          color: '#2b2340',
                          borderRadius: 2.5,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.09),
                            color: theme.palette.primary.main,
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        Setores
                      </Button>
                    )}
                    {/* <Button 
                      component={Link} 
                      to="/dashboard/produtos" 
                      sx={{ 
                        my: 2, 
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Produtos
                    </Button>
                    */}
                  </>
                )}
                {/* Link para cadastro público (organizadores) — Meu Sacramento */}
                {!isAuthenticated && !isCustomBrand && (
                  <Button 
                    component={Link} 
                    to="/cadastro-paroquia" 
                    variant="contained"
                    endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{ 
                      my: 1.2,
                      color: 'white',
                      fontWeight: 700,
                      borderRadius: 999,
                      px: 2.1,
                      py: 0.78,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      letterSpacing: '0.01em',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #6a3ba0 100%)`,
                      boxShadow: '0 8px 18px rgba(75, 27, 122, 0.22)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #5f2f95 100%)`,
                        boxShadow: '0 10px 22px rgba(75, 27, 122, 0.3)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Cadastrar evento
                  </Button>
                )}
              </Box>

              <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isAuthenticated ? (
                  <>
                    <IconButton
                      onClick={handleTopbarMenuOpen}
                      sx={{ 
                        p: 0,
                        '&:hover': {
                          transform: 'scale(1.05)'
                        },
                        transition: 'transform 0.2s ease'
                      }}
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                    >
                      <Avatar 
                        alt={user?.name} 
                        sx={{
                          border: '2px solid rgba(255,255,255,0.3)'
                        }}
                      />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Button
                      component={Link}
                      to="/login"
                      variant={isClubPublic ? 'text' : 'contained'}
                      sx={
                        isClubPublic
                          ? {
                              color: '#2b2340',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                              fontSize: { xs: '0.75rem', md: '0.85rem' },
                              '&:hover': { color: '#B84D4D', bgcolor: 'transparent' },
                            }
                          : {
                              ml: 1.5,
                              background: theme.palette.primary.main,
                              color: 'white',
                              borderRadius: 999,
                              fontWeight: 600,
                              px: 2.2,
                              py: 0.72,
                              minWidth: 74,
                              fontSize: '0.78rem',
                              textTransform: 'none',
                              '&:hover': {
                                background: theme.palette.primary.dark,
                                boxShadow: '0 6px 16px rgba(58, 26, 93, 0.24)'
                              },
                              transition: 'all 0.2s ease'
                            }
                      }
                    >
                      Entrar
                    </Button>
                  </>
                )}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      )}

      {isAuthenticated && (
        <>
        <Menu
          id="menu-topbar-account"
          anchorEl={topbarMenuAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(topbarMenuAnchorEl)}
          onClose={handleClose}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <MenuItem component={Link} to="/dashboard/perfil" onClick={handleClose} sx={{ gap: 1 }}>
            <PersonOutlineIcon fontSize="small" />
            Perfil
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ gap: 1, color: 'error.main' }}>
            <LogoutIcon fontSize="small" />
            Sair
          </MenuItem>
        </Menu>
        <Menu
          id="menu-sidebar-account"
          anchorEl={sidebarMenuAnchorEl}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={Boolean(sidebarMenuAnchorEl)}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                mt: -1,
                minWidth: 180,
                borderRadius: 2.5,
                p: 0.5,
                boxShadow: '0 14px 34px rgba(15, 23, 42, 0.18)',
                border: '1px solid',
                borderColor: 'divider',
              },
            },
          }}
        >
          <MenuItem
            component={Link}
            to="/dashboard/perfil"
            onClick={handleClose}
            sx={{ borderRadius: 1.5, gap: 1 }}
          >
            <PersonOutlineIcon fontSize="small" />
            Perfil
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ borderRadius: 1.5, gap: 1, color: 'error.main' }}>
            <LogoutIcon fontSize="small" />
            Sair
          </MenuItem>
        </Menu>
        </>
      )}

      {/* Drawer lateral (mobile) */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
          {dashboardMenuContent}
        </Box>
      </Drawer>

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          minHeight: isParishDashboardShell ? { xs: 'calc(100vh - 64px)', md: '100vh' } : 'calc(100vh - 64px)',
          display: 'flex',
        }}
      >
        {isParishDashboardShell && (
          <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, alignSelf: 'flex-start', height: '100vh' }}>
            {dashboardMenuContent}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {isParishDashboardShell && showOnboardingBanner ? (
            <Alert
              severity={user?.parishAdminOnboarding?.emailVerified ? 'info' : 'warning'}
              sx={{ mx: { xs: 1.5, md: 2 }, mt: 2, borderRadius: 2 }}
              action={
                <Button component={Link} to="/dashboard/onboarding" color="inherit" size="small">
                  Ver passos
                </Button>
              }
            >
              {user?.parishAdminOnboarding?.emailVerified
                ? `Complete seu cadastro para liberar todas as funcionalidades (${user.parishAdminOnboarding.progressPercent}% concluído).`
                : 'Seu e-mail ainda não consta como confirmado. Recarregue a página ou abra «Ver passos» — quem já entrou com senha deve ser liberado automaticamente.'}
            </Alert>
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Box>
      </Box>

      {isParishDashboardShell && (
        <Paper
          elevation={8}
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            left: 8,
            right: 8,
            bottom: 8,
            borderRadius: 3,
            zIndex: 1200,
            overflow: 'hidden',
          }}
        >
          <BottomNavigation
            showLabels={false}
            value={mobileBottomValue}
            onChange={(_, newValue) => navigate(newValue)}
            sx={{
              height: 64,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 56,
                color: 'text.secondary',
              },
              '& .MuiBottomNavigationAction-label': {
                display: 'none',
              },
              '& .Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
            }}
          >
            {mobileBottomItems.map((item) => (
              <BottomNavigationAction key={item.path} label={item.label} value={item.path} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}

      {/* Rodapé: em páginas personalizadas, mantém links legais sem branding religioso/Meu Sacramento */}
      <Box
        component="footer"
        sx={{
          display: isParishDashboardShell ? { xs: 'none', md: 'block' } : 'block',
          py: 6,
          px: 2,
          mt: 'auto',
          background: isNeutralChrome
            ? 'linear-gradient(135deg, #111827 0%, #1f2937 100%)'
            : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: isNeutralChrome
              ? 'linear-gradient(90deg, #4324F6 0%, #F2B33D 100%)'
              : 'linear-gradient(90deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)'
          }
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            {!isNeutralChrome ? (
              <Box display="flex" alignItems="center">
                <Box
                  component="img"
                  src={brandLogo}
                  alt={`Logo ${brandName}`}
                  sx={{
                    width: isCustomBrand ? { xs: 220, sm: 280 } : 156,
                    height: isCustomBrand ? { xs: 72, sm: 96 } : 34,
                    objectFit: 'contain',
                    display: 'block',
                    filter: isCustomBrand ? 'none' : 'brightness(0) invert(1) opacity(0.92)',
                  }}
                />
              </Box>
            ) : (
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {organizerName || 'Eventos'}
              </Typography>
            )}
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              {isNeutralChrome
                ? `© ${new Date().getFullYear()}${organizerName ? ` ${organizerName}` : ''}`
                : brand.copy.chrome.footerCopyright}
            </Typography>
          </Box>
          
          {/* Links Legais */}
          <Box mt={3} mb={2} textAlign="center">
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
              <Button 
                component={Link} 
                to="/termos-de-uso" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Termos de Uso
              </Button>
              <Button 
                component={Link} 
                to="/politica-de-privacidade" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Política de Privacidade
              </Button>
              <Button 
                component={Link} 
                to="/politica-de-reembolso" 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Política de Reembolso
              </Button>
              {brand.copy.chrome.showSupportEmail && brand.copy.chrome.supportEmail && (
              <Button 
                href={`mailto:${brand.copy.chrome.supportEmail}`} 
                sx={{ 
                  color: 'rgba(255,255,255,0.8)',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  '&:hover': { 
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Suporte
              </Button>
              )}
            </Box>
          </Box>
          
          {!isNeutralChrome && brand.copy.chrome.showAcutisQuote && (
            <Box mt={2} textAlign="center">
              <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ fontStyle: 'italic' }}>
                "A Eucaristia é a minha autoestrada para o Céu." — Beato Carlo Acutis
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default MainLayout;

